import React, { useEffect, useRef } from 'react';
import { FormControl, FormControlLabel, FormGroup, FormHelperText } from '@mui/material';
import { useFormField } from '@core/hooks/useFormField';
import { WcnCheckbox } from '@/components';

type ValidationRule = (value: any) => string;

export interface FormCheckboxGroupItem {
    key: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: React.ReactNode;
    disabled?: boolean;
}

export interface FormCheckboxGroupProps {
    name: string;
    value: string[];
    items: FormCheckboxGroupItem[];
    rules?: ValidationRule[];
    helperText?: string;
    row?: boolean;
    size?: 'small' | 'medium' | 'large';
    className?: string;
    label?: string;
}

const FormCheckboxGroup: React.FC<FormCheckboxGroupProps> = ({
    name,
    value,
    items,
    rules = [],
    helperText: staticHelperText,
    row = true,
    size = 'small',
    className,
    label
}) => {
    const { error, showError, handleBlur } = useFormField({
        name,
        label,
        value,
        rules
    });

    const valueKey = [...value].sort().join('|');
    const isFirstRender = useRef(true);
    const prevValueKey = useRef(valueKey);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevValueKey.current = valueKey;
            return;
        }
        if (prevValueKey.current === valueKey) return;
        prevValueKey.current = valueKey;
        handleBlur();
    }, [valueKey]);

    return (
        <FormControl error={showError} component="fieldset" sx={{ width: '100%', minWidth: 0 }}>
            <FormGroup row={row} className={className}>
                {items.map((item) => (
                    <FormControlLabel
                        key={item.key}
                        control={
                            <WcnCheckbox
                                checked={item.checked}
                                onChange={(e) => item.onChange(e.target.checked)}
                                disabled={item.disabled}
                                size={size}
                            />
                        }
                        label={item.label}
                    />
                ))}
            </FormGroup>
            {showError && error ? (
                <FormHelperText>{error}</FormHelperText>
            ) : staticHelperText ? (
                <FormHelperText>{staticHelperText}</FormHelperText>
            ) : null}
        </FormControl>
    );
};

FormCheckboxGroup.displayName = 'FormCheckboxGroup';

export default FormCheckboxGroup;
